import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '@/components/button';
import { ErrorFallback, FallbackScreen } from '@/components/error-fallback';
import {
  clearChunkReloadFlag,
  hasAttemptedChunkReload,
  isChunkLoadError,
  recoverFromChunkLoadError,
} from '@/utils/chunk-load-error';

const Container = styled.div`
  margin: 0 auto;
  max-width: 500px;
  text-align: center;
  padding: 4em 1.5em;
  margin-bottom: 300px;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.theme.colors.background.primary};
  padding: 1.5em;
`;

const Message = styled.p`
  color: ${(props) => props.theme.colors.typography.primary};
  line-height: 1.5;
  margin: 0 0 1.5em;
`;

function handleReload() {
  clearChunkReloadFlag();
  window.location.reload();
}

const RELOAD_TITLE = 'New version available';
const RELOAD_MESSAGE = 'This page is out of date. Please reload to continue.';
const RELOAD_LABEL = 'Reload';

/**
 * "Reload to get the new version" prompt.
 *
 * `asOverlay` is the in-app dialog (_app.js renders it inside the providers,
 * so it may use the theme). Without it, this is rendered by the error-boundary
 * fallback below, where no provider or design token exists: that branch must
 * stay context-free (see error-fallback.js).
 */
export function ChunkLoadReloadPrompt({ asOverlay = false }) {
  if (!asOverlay) {
    return (
      <FallbackScreen
        message={RELOAD_MESSAGE}
        onReload={handleReload}
        reloadLabel={RELOAD_LABEL}
        title={RELOAD_TITLE}
      />
    );
  }

  return (
    <Overlay role="alertdialog" aria-labelledby="chunk-reload-title">
      <Container>
        <h1 id="chunk-reload-title">{RELOAD_TITLE}</h1>
        <Message>{RELOAD_MESSAGE}</Message>
        <Button primary onClick={handleReload}>
          {RELOAD_LABEL}
        </Button>
      </Container>
    </Overlay>
  );
}

/**
 * Bugsnag / React ErrorBoundary FallbackComponent.
 * Auto-reloads once for chunk load errors; otherwise shows the error screen.
 *
 * The boundary wraps NextIntlClientProvider, the colour-mode provider and
 * GlobalStyle, so nothing rendered here may depend on them: every branch uses
 * the context-free screens from error-fallback.js. Do not render the /_error
 * page here (it needs next-intl and the design tokens).
 */
export function ChunkLoadRecoveryFallback({ error }) {
  const [mode, setMode] = useState(() => {
    if (!isChunkLoadError(error)) return 'passthrough';
    return hasAttemptedChunkReload() ? 'prompt' : 'reloading';
  });

  useEffect(() => {
    if (mode !== 'reloading') return;
    const result = recoverFromChunkLoadError();
    if (result === 'prompt') {
      setMode('prompt');
    }
  }, [mode]);

  if (mode === 'passthrough') {
    return <ErrorFallback />;
  }

  if (mode === 'reloading') {
    return <FallbackScreen message="Loading the latest version of this page." title="Updating…" />;
  }

  return <ChunkLoadReloadPrompt />;
}

export default ChunkLoadRecoveryFallback;
